'use client';

import TitleBar from "../../components/title-bar";
import { CardFantasyItem, LineupStatus } from "../../services/home";
import { useRouter } from '@/i18n/navigation';
import { useCountDown } from 'ahooks';
import { useMemo, useState, type ReactNode, memo, useRef, useCallback } from 'react';
import IconTime from '@/features/home/images/fantasy/time.svg';
import LineOutImg from '@/features/home/images/fantasy/linesup-normal.svg';
import { useTranslations } from 'next-intl';
import { useBalanceFormatter } from '@/hooks/use-balance-formatter';
import { useAtomValue, useSetAtom } from 'jotai';
import { isLoginAtom, selectedCurrencyAtom } from '@/store';
import dayjs from 'dayjs';
import { menuTabUrl } from '@/const/page-url';
import useFn from '@/hooks/use-fn';
import { track } from '@/utils/track';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CURRENCY_SHOW_MAP } from '@/const/currency';
import { useInViewport } from 'ahooks';
import { useFantasyList } from '../../services/fantasy';
import { Spin } from '@/components/ui-feat/spin';
import FantasyCardWithExposure from './components/fantasy-card-with-exposure';
import FantasyMatchCard from './components/fantasy-match-card';
import { fantasyGameUrlAtom } from '@/app/[locale]/(main)/(non-activity)/fantasy/store';
interface FantasyProps {
  // fantasyData: {
  //   matches: CardFantasyItem[];
  // };
  onTimeout?: () => void;
  // title: string;
}


export default function FantasyCard(props: FantasyProps) {
  // const { fantasyData = { matches: [] }, title = '', onTimeout } = props;
  const { onTimeout } = props;
  // 视口检测：用于懒加载图片资源
  const containerRef = useRef<HTMLDivElement>(null);
  const [inViewport] = useInViewport(containerRef, {
    rootMargin: '100px 0px', // 提前100px开始加载，提升用户体验
  });

  // 记录已经触发过 onTimeout 的 card ID，确保每个 card 到期只请求一次
  const triggeredCardIdsRef = useRef<Set<string>>(new Set());

  const { data: fantasyData, isLoading } = useFantasyList.useQueryHook({});

  // 解析 fantasyData content 数据（可能是 JSON 字符串）
  const parsedData = useMemo(() => {
    if (!fantasyData?.content) {
      return null;
    }
    try {
      const parsed = typeof fantasyData.content === 'string' ? JSON.parse(fantasyData.content) : fantasyData.content;
      return parsed ? parsed?.matches as CardFantasyItem[] : null;
    } catch (error) {
      console.error('Failed to parse fantasyData content:', error);
      return null;
    }
  }, [fantasyData]);

  // 稳定的函数引用，避免 MatchStatus 组件不必要的重新渲染
  const hasTriggered = useCallback((id: string) => {
    return triggeredCardIdsRef.current.has(id);
  }, []);

  const markAsTriggered = useCallback((id: string) => {
    triggeredCardIdsRef.current.add(id);
  }, []);

  const t = useTranslations('home');
  const { formatCurrencyDetail } = useBalanceFormatter();
  const isLogin = useAtomValue(isLoginAtom);
  const router = useRouter();

  // 独立的 Popover 组件，管理自己的状态，避免影响父组件重新渲染
  const LineupStatusPopover = () => {
    const [open, setOpen] = useState(false);

    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <div
            className="relative z-10 cursor-pointer"
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <LineOutImg fill={'var(--text-tertiary)'} />
          </div>
        </PopoverTrigger>
        <PopoverContent className="translate-x-4" align='end' side='top'>
          <div>{t('notLinedUp')}</div>
        </PopoverContent>
      </Popover>
    );
  };

  const getMatchStatusLines = (
    startTime: string | number,
    leftSeconds?: number
  ): { lineOne: ReactNode; lineTwo: ReactNode; } => {
    const startTimestamp = Number(startTime);
    const startDate = dayjs.unix(startTimestamp);
    const currentDate = dayjs();

    const computedSeconds =
      typeof leftSeconds === 'number' ? leftSeconds : startTimestamp - currentDate.unix();
    const distanceInSeconds = Math.max(computedSeconds, 0);
    const distanceInHours = distanceInSeconds / 3600;
    const distanceInMinutes = distanceInSeconds / 60;

    // Get midnight (0 o'clock) of current day
    const todayMidnight = currentDate.startOf('day');
    const startDateMidnight = startDate.startOf('day');

    // Calculate days difference (0 = today, 1 = tomorrow, 2+ = future date)
    const daysDifference = startDateMidnight.diff(todayMidnight, 'day');

    let lineOne: ReactNode;
    let lineTwo: ReactNode;

    // Format time as Hour+Minute+AM/PM (reusable function)
    const formatTime12Hour = () => startDate.format('h:mm A');

    // Distance > 9 hours (judged by 0 o'clock)
    if (distanceInHours > 9) {
      // Determine based on calendar day difference
      if (daysDifference === 0) {
        // Same calendar day: "Today"
        lineOne = <span className='text-white text-ss font-medium'>{t('today')}</span>;
      } else if (daysDifference === 1) {
        // Next calendar day: "Tomorrow"
        lineOne = <span className='text-white text-ss font-medium'>{t('tomorrow')}</span>;
      } else {
        // More than 2 calendar days: show date + month (e.g., "25 Apr")
        lineOne = <span className='text-white text-ss font-medium'>{startDate.format('D MMM')}</span>;
      }

      // Format time as Hour+Minute+am/pm
      lineTwo = <span className='text-tertiary text-xss font-medium'>{formatTime12Hour()}</span>;
    }
    // 1 hour < distance ≤ 9 hours
    else if (distanceInHours > 1 && distanceInHours <= 9) {
      // Countdown: Hour+Minute
      const hours = Math.floor(distanceInHours);
      const minutes = Math.floor((distanceInHours - hours) * 60);
      const minutesStr = String(minutes).padStart(2, '0');
      lineOne = (
        <span className='text-accent-red text-ss font-medium inline-block text-right whitespace-nowrap'>
          {String(hours).split('').map((digit, idx) => (
            <span key={`h-${idx}`} className='inline-block w-[8px] text-right'>{digit}</span>
          ))}
          h{' '}
          {minutesStr.split('').map((digit, idx) => (
            <span key={`m-${idx}`} className='inline-block w-[8px] text-right'>{digit}</span>
          ))}
          m
        </span>
      );

      // If lineups are out, show "Lineups Out", otherwise show time
      lineTwo = <span className='text-tertiary text-xss font-medium'>{formatTime12Hour()}</span>;
    }
    // 0 < distance ≤ 1 hour
    else if (distanceInMinutes > 0 && distanceInHours <= 1) {
      // Countdown: Minute+Second
      const minutes = Math.floor(distanceInMinutes);
      const seconds = Math.floor(distanceInSeconds % 60);
      const secondsStr = String(seconds).padStart(2, '0');
      lineOne = (
        <span className='text-accent-red text-ss font-medium pl-[7px] inline-block text-right whitespace-nowrap'>
          {minutes > 0 && (
            <>
              {String(minutes).split('').map((digit, idx) => (
                <span key={`min-${idx}`} className='inline-block w-[8px] text-right'>{digit}</span>
              ))}
              m{' '}
            </>
          )}
          {secondsStr.split('').map((digit, idx) => (
            <span key={`s-${idx}`} className='inline-block w-[8px] text-right'>{digit}</span>
          ))}
          s
        </span>
      );

      // If lineups are out, show "Lineups Out", otherwise show time
      lineTwo = <span className='text-tertiary text-xss font-medium'>{formatTime12Hour()}</span>;
    }
    // Match has started or invalid time
    else {
      lineOne = <div className='flex items-center text-white text-ss font-medium'>
        <IconTime />
        {t('extended')}</div>;
      lineTwo = '';
    }

    return { lineOne, lineTwo };
  };

  const MatchStatus = memo(({
    startTime,
    children,
    onTimeout,
    cardId,
    hasTriggered,
    markAsTriggered,
  }: {
    startTime: string | number;
    children: (status: { lineOne: string; lineTwo: string; }) => ReactNode;
    onTimeout?: () => void;
    cardId: string;
    hasTriggered: (id: string) => boolean;
    markAsTriggered: (id: string) => void;
  }) => {
    const targetDate = Number(startTime) * 1000;
    const [countdown] = useCountDown({
      targetDate,
      interval: 1000,
      onEnd: () => {
        // 检查该 card 是否已经触发过，如果已经触发过，就不再调用 onTimeout
        if (!hasTriggered(cardId)) {
          markAsTriggered(cardId);
          onTimeout?.();
        }
      },
    });

    const status = useMemo(() => {
      return getMatchStatusLines(startTime, Math.max(0, countdown) / 1000);
    }, [countdown, startTime]);

    return <>{children(status as { lineOne: string; lineTwo: string; })}</>;
  });

  MatchStatus.displayName = 'MatchStatus';

  const isLineupStatusOut = (lineupStatus: LineupStatus) => {
    return lineupStatus === LineupStatus.LINEUP_STATUS_OUT ? true : false;
  };

  const selectedCurrency = useAtomValue(selectedCurrencyAtom);
  const renderCurrency = useCallback((currencyList: Array<{ currency_type: string; price_pool: number; }>) => {
    // 产品要求，未登录默认INR
    const curCurrency = isLogin ? selectedCurrency : 'INR';
    const items = currencyList.find(item => item.currency_type === curCurrency);
    // USDT 特殊处理
    if (items?.currency_type === 'USDT') {
      return formatCurrencyDetail(items.price_pool, {
        applyKMBT: true,
      }) + ' USDT';
    }
    return items ? CURRENCY_SHOW_MAP[items.currency_type] + formatCurrencyDetail(items.price_pool, {
      applyKMBT: true,
    }) : '';
  }, [selectedCurrency, formatCurrencyDetail]);

  const setFantasyGameUrl = useSetAtom(fantasyGameUrlAtom);
  // 跳全部赛事
  const handleViewAll = useFn(() => {
    // 需要销毁 sports tab页
    setFantasyGameUrl('');
    router.push(menuTabUrl.fantasy + '?trackSource=fantasy');
    track('container_button_click', {
      current_page: 'Home',
      container_name: 'fantasy',
      button_name: 'View All'
    });
  });

  // 直接跳联赛（一个联赛有多个赛事）
  const handleTourClick = useFn((item: CardFantasyItem) => {
    router.push(`${menuTabUrl?.fantasy}?trackSource=fantasy&tourId=${item?.tour_id}`);
  });
  return (
    <>
      <div ref={containerRef} className='px-[12px]'>
        <TitleBar
          colors={['#35D5FF', '#0081C3']}
          rightTitleClassName='!pr-0'
          leftTitle={'Fantasy'}
          rightTitle={t('viewAll')}
          onClickRightTitle={handleViewAll}
        />
        {
          isLoading ? (
            <div className="space-y-1">
              <Spin className="h-40 w-full rounded-lg" />
            </div>
          ) : <>
            {
              parsedData?.map((item, index) => (
                <MatchStatus
                  startTime={item?.start_time || 0}
                  key={item.id || index}
                  cardId={item.id}
                  onTimeout={onTimeout}
                  hasTriggered={hasTriggered}
                  markAsTriggered={markAsTriggered}
                >
                  {({ lineOne, lineTwo }) => (
                    <FantasyCardWithExposure
                      item={item}
                      index={index}
                    >
                      {({ cardRef, handleNavigate }) => (
                        <FantasyMatchCard
                          key={index}
                          item={item}
                          index={index}
                          lineOne={lineOne}
                          lineTwo={lineTwo}
                          inViewport={!!inViewport}
                          isLogin={isLogin}
                          renderCurrency={renderCurrency}
                          isLineupStatusOut={isLineupStatusOut}
                          LineupStatusPopover={LineupStatusPopover}
                          handleTourClick={handleTourClick}
                          cardRef={cardRef}
                          handleNavigate={handleNavigate}
                          t={t}
                        />
                      )}
                    </FantasyCardWithExposure>
                  )}
                </MatchStatus>
              ))
            }
          </>
        }
      </div >
    </>
  );
};
